import {types, Instance, SnapshotIn, SnapshotOut, getParentOfType, addDisposer, getEnv} from 'mobx-state-tree';
import intl from "react-intl-universal";

import {nanoid} from 'nanoid';
import {reaction} from "mobx";

import {Item, ItemType, WorkspaceUser} from "../API";

import {updateItemChildIdsAdd} from "../actions/item/updateItem";
import {
    updateWorkspaceArchivedIdsRemove,
    updateWorkspaceChildIdsAdd,
    updateWorkspaceChildIdsRemove,
    updateWorkspaceTrashedIdsRemove
} from "../actions/workspace/updateWorkspace";

import {captureMessageWrapper} from "../utils/errors/captureMessage";
import {adjustWorkspaceItems, itemToItemMSTSnapshotIn} from "./convertors/itemToItemMSTSnapshotIn";
import {DocumentTreeSource} from "../ui/elements/ListView/DocumentTreeSource";
import {RootStoreEnv} from "@stores/createStore";
import {CrudServices} from "../platform/services/types/ServiceDependencies";
import type {AppState} from "@stores/AppState";
import {IAuthService} from "../platform/services/auth/AuthService";
import {IDataClientService} from "../platform/services/data/DataClientService";
import {assertCanContainChild, getDefaultCreateItemParentId} from "../actions/item/itemParenting";


export const ItemModel = types.model('ItemModel', {
    id: types.optional(types.identifier, () => nanoid()),
    workspaceId: types.string,
    name: types.optional(types.string, ''),
    slug: types.optional(types.maybeNull(types.string), null),
    creator: types.string,
    description: types.optional(types.string, ''),
    createdAt: types.string,
    updatedAt: types.optional(types.string, ''),
    updatedBy: types.optional(types.maybeNull(types.string), null),
    isExpanded: types.optional(types.boolean, false),
    isRenaming: types.optional(types.boolean, false),
    trashed: types.optional(types.boolean, false),
    archived: types.optional(types.boolean, false),
    pinned: types.optional(types.boolean, false),
    pinnedIds: types.optional(types.array(types.string), []),
    acknowledgementRequired: types.optional(types.boolean, false),
    acknowledgement: types.optional(types.frozen(), {}),
    type: types.string,
    childIds: types.optional(types.array(types.string), []),
    parentIds: types.optional(types.array(types.string), []),
    statusSourceLibraryId: types.optional(types.maybeNull(types.string), null),
    statusRestoreParentIds: types.optional(types.array(types.string), []),
    style: types.optional(types.string, ''),
    lastSyncedVersion: types.optional(types.number, 0),
})
    .views(self => ({
        getParentPaths(ancestors: Set<string> | null = null): string[][] {
            const workspace = getParentOfType(self, WorkspaceModel);
            const paths: string[][] = [];
            workspace.items.forEach(item => {
                if (
                    item.trashed !== self.trashed
                    || item.archived !== self.archived
                ) {
                    return;
                }
                if (item.childIds.find(id => self.id === id) !== undefined) {
                    let _ancestors = ancestors;
                    if (_ancestors === null) {
                        _ancestors = new Set();
                    }
                    if (!_ancestors.has(item.id)) {
                        _ancestors.add(item.id);
                        const parentParentPaths = item.getParentPaths(_ancestors);
                        if (parentParentPaths.length > 0) {
                            for (const parentParentPath of parentParentPaths) {
                                paths.push([...parentParentPath, item.id]);
                            }
                        } else {
                            paths.push([item.id]);
                        }
                    }
                }
            })
            return paths;
        },
        get isCluster(): boolean {
            return self.type === ItemType.CLUSTER;
        },
        get isLeaf(): boolean {
            return self.type === ItemType.LEAF;
        },
        get isLibrary(): boolean {
            return self.type === ItemType.LIBRARY;
        },
        get parents(): any[] {
            const workspace: any = getParentOfType(self, WorkspaceModel)!;
            return self.parentIds.map(id => workspace.items.get(id));
        },
        get defaultView(): ViewType {
            return self.trashed ? 'trash' : (
                self.archived ? 'archive' : 'main'
            );
        },
        get confirmedUsers() {
            const dsManager = getEnv<RootStoreEnv>(self).getDsManager()!;

            const workspaceUsers: WorkspaceUser[] = dsManager!.getWsUsersDataSource(self.workspaceId)?.allRows || [];
            return workspaceUsers?.filter(wsUser =>
                self.acknowledgement?.[wsUser.userId] !== undefined
            ) || [];
        },
        get unConfirmedUsers() {
            const dsManager = getEnv<RootStoreEnv>(self).getDsManager()!;
            const workspaceUsers: WorkspaceUser[] = dsManager!.getWsUsersDataSource(self.workspaceId)?.allRows || [];
            return workspaceUsers?.filter(wsUser =>
                self.acknowledgement?.[wsUser.userId] === undefined
            ) || [];
        }
    }))
    .views(self => ({
        get firstParentId(): string | null {
            return self.getParentPaths().length > 0 ? self.getParentPaths()[0][0] : null;
        },
        getNamePlaceholder(locale: string): string {
            return self.isLeaf && locale
                ? intl.get('common.untitled_item').d('Untitled page')
                : intl.get('common.untitled_cluster').d('Untitled cluster')
        },
        get activeParentPaths(): string[][] {
            const workspace: any = getParentOfType(self, WorkspaceModel)!;
            return self.getParentPaths().filter(
                parentPath => parentPath.map(itemId => workspace.items.get(itemId)!)
                    .every(item => !item.trashed && !item.archived)
            )
        },
    }))
    .views(self => ({
        getNameWithPlaceholder(locale: string): string {
            return self.name
                ? self.name
                : self.getNamePlaceholder(locale)
        },
    }))
    .actions(self => ({
            setStyle(newValue: string) {
                self.style = newValue;
            },
            setSyncedVersion(version: number) {
                self.lastSyncedVersion = version;
            },
            setIsRenaming(newValue: boolean) {
                self.isRenaming = newValue;
            },
            setName(newValue: string) {
                self.name = newValue;
            },
            setSlug(newValue: string | null) {
                self.slug = newValue;
            },
            setDescription(newValue: string) {
                self.description = newValue;
            },
            setUpdatedAt(newValue: string) {
                self.updatedAt = newValue;
            },
            setUpdatedBy(newValue: string | null) {
                self.updatedBy = newValue;
            },
            setPinned(newValue: boolean) {
                self.pinned = newValue;
            },
            setPinnedIds(item_ids: string[]) {
                // @ts-ignore
                self.pinnedIds = item_ids;
            },
            addPinnedId(item_id: string, index?: number) {
                if (index !== undefined) {
                    self.pinnedIds.splice(index, 0, item_id);
                } else {
                    self.pinnedIds.push(item_id);
                }
            },
            removePinnedId(item_id: string) {
                self.pinnedIds.remove(item_id);
            },
            setArchived(newValue: boolean) {
                self.archived = newValue;
            },
            setTrashed(newValue: boolean) {
                self.trashed = newValue;
            },
            setExpanded(newValue: boolean) {
                self.isExpanded = newValue;
            },
            setAcknowledgementRequired(newValue: boolean) {
                self.acknowledgementRequired = newValue;
                self.acknowledgement = {};
            },
            markAcknowledgement(userId: string) {
                self.acknowledgement = {
                    ...self.acknowledgement,
                    [userId]: {
                        date: new Date().toISOString()
                    }
                }
            },
            setAcknowledgement(acknowledgement: any) {
                self.acknowledgement = acknowledgement
            },
            clearAcknowledgements() {
                self.acknowledgementRequired = false;
                self.acknowledgement = {};
            },
            setChildIds(item_ids: string[]) {
                // @ts-ignore
                self.childIds = item_ids;
            },
            addChild(item_id: string, index?: number) {
                if (index !== undefined) {
                    self.childIds.splice(index, 0, item_id);
                } else {
                    self.childIds.push(item_id);
                }
            },
            addParent(item_id: string) {
                if (!self.parentIds.includes(item_id)) {
                    self.parentIds.push(item_id);
                }
            },
            removeChild(targetItemId: string) {
                self.childIds.remove(targetItemId);
            },
            setParentIds(item_ids: string[]) {
                // @ts-ignore
                self.parentIds = item_ids;
            },
            setStatusSourceLibraryId(libraryId: string | null) {
                self.statusSourceLibraryId = libraryId;
            },
            setStatusRestoreParentIds(parentIds: string[]) {
                // @ts-ignore
                self.statusRestoreParentIds = parentIds;
            },
            removeParent(targetItemId: string) {
                while (self.parentIds.includes(targetItemId)) {
                    self.parentIds.remove(targetItemId);
                }
            },
        })
    );

export type ViewType = 'main' | 'active' | 'recent' | 'trash' | 'archive';

const STORAGE_NAME_FOR_RECENT_ITEMS = 'recent-items';

export const WorkspaceModel = types.model('WorkspaceModel',
    {
        id: types.optional(types.identifier, () => nanoid()),
        name: types.optional(types.string, ''),
        slug: types.optional(types.string, ''),
        items: types.optional(types.map(ItemModel), {}),
        childIds: types.optional(types.array(types.string), []),
        trashedIds: types.optional(types.array(types.string), []),
        archivedIds: types.optional(types.array(types.string), []),
        selectedId: types.optional(types.maybeNull(types.string), null),
        selectedLibraryId: types.optional(types.maybeNull(types.string), null),
        view: types.optional(types.string, 'main'),
        isReady: types.optional(types.boolean, false),
        scrollIntoViewId: types.optional(types.maybeNull(types.string), null),
        lastSyncedVersion: types.optional(types.number, 0),
        recentIds: types.optional(types.frozen<string[]>(), []),
        hiddenItems: types.optional(types.array(types.string), []), /* ui improvement on trash Item action */
        confirmDeleteOpenUnder: types.optional(types.maybe(types.string), undefined),
        avatarPath: types.maybeNull(types.string)
    })
    .views(self => ({
        get selected(): ItemModelType | null {
            if (self.selectedId === null) {
                return null;
            }
            const selected = self.items.get(self.selectedId);
            if (selected === undefined) {
                captureMessageWrapper(`not found selected ${self.selectedId}`);
                return null;
            }
            return selected;
        },
        get libraries(): ItemModelType[] {
            return getVisibleLibraries(self);
        },
        get selectedLibrary(): ItemModelType | null {
            const selectedLibrary = self.selectedLibraryId
                ? self.items.get(self.selectedLibraryId)
                : null;

            if (
                selectedLibrary
                && selectedLibrary.isLibrary
                && !selectedLibrary.trashed
                && !selectedLibrary.archived
            ) {
                return selectedLibrary;
            }

            return getVisibleLibraries(self)[0] ?? null;
        },
        findLibraryIdForItem(itemId: string): string | null {
            const item = self.items.get(itemId);
            if (!item) {
                return null;
            }
            if (item.isLibrary && !item.trashed && !item.archived) {
                return item.id;
            }

            const libraryIds = new Set(getVisibleLibraries(self).map(library => library.id));
            const directLibraryParentId = item.parentIds.find(parentId => libraryIds.has(parentId));
            if (directLibraryParentId) {
                return directLibraryParentId;
            }

            const pathWithLibrary = item.activeParentPaths.find(parentPath =>
                parentPath.some(parentId => libraryIds.has(parentId))
            );
            if (!pathWithLibrary) {
                return null;
            }

            return pathWithLibrary.find(parentId => libraryIds.has(parentId)) ?? null;
        },
        findLibraryBySlugOrId(slugOrId: string | null | undefined): ItemModelType | null {
            if (!slugOrId) {
                return null;
            }

            return getVisibleLibraries(self).find(library =>
                library.slug === slugOrId || library.id === slugOrId
            ) ?? null;
        },
        getStatusSourceLibraryIdForItem(itemId: string): string | null {
            return getStatusSourceLibraryIdForItem(self, itemId);
        },
        get trashedItems() {
            return Array.from(self.items.values()).filter(item => item.trashed === true);
        },
        get abbreviate(): string {
            const name = self.name;
            return `${name.charAt(0)}${name.charAt(1)}`;
        },
    }))
    .actions(self => ({
        pushToRecent(itemId: string) {
            const item = self.items.get(itemId);
            if (!item || !item.isLeaf) {
                return;
            }
            const newRecent = self.recentIds.filter(recentId => recentId !== itemId);
            newRecent.unshift(itemId);
            /* @ts-ignore */
            self.recentIds = newRecent.slice(0, 200);
        },
        removeFromRecent(itemId: string) {
            self.recentIds = self.recentIds.filter(recentId => recentId !== itemId);
        },
        readFromLocalStorage() {
            let recentIds = window.localStorage.getItem(STORAGE_NAME_FOR_RECENT_ITEMS);
            if (recentIds) {
                self.recentIds = JSON.parse(recentIds);
            }
        },
        setConfirmDeleteOpen(itemPath: string | undefined) {
            self.confirmDeleteOpenUnder = itemPath;
        },
    }))
    .actions(self => ({
        setName(name: string) {
            self.name = name;
        },
        setSlug(slug: string) {
            self.slug = slug;
        },
        removeItemSync(itemId: string) {
            self.removeFromRecent(itemId);
            self.items.delete(itemId);
        },
        addItemSync(item: ItemModelSnapshotIn) {
            self.items.put(item);
        },
        afterCreate() {
            if (typeof window.localStorage !== "undefined") {
                self.readFromLocalStorage();
                const disposer = reaction(
                    () => self.recentIds,
                    recentIds => {
                        window.localStorage.setItem(STORAGE_NAME_FOR_RECENT_ITEMS, JSON.stringify(recentIds))
                    }
                );
                addDisposer(self, disposer);
            }
        },
        setSelectedId(selectedId: string | null) {
            self.selectedId = selectedId;
        },
        setSelectedLibraryId(selectedLibraryId: string | null) {
            self.selectedLibraryId = selectedLibraryId;
        },
        setScrollIntoViewId(itemId: string | null) {
            self.scrollIntoViewId = itemId;
        },
        setView(nextView: ViewType) {
            self.view = nextView;
        },
        hideTreeItem(itemId: string) {
            self.hiddenItems.push(itemId);
        },
        cancelHideTreeItem(itemId: string) {
            self.hiddenItems.remove(itemId);
        },
    }))
    .actions(self => ({
        setSyncedVersion(version: number) {
            self.lastSyncedVersion = version;
        },
        setIsReady(ready: boolean = true) {
            self.isReady = ready;
        },
        setItems(items: Item[]) {
            self.items = adjustWorkspaceItems(items.map(itemToItemMSTSnapshotIn))
            self.isReady = true;
        },
        syncItems(itemsToSync: ItemModelSnapshotIn[]) {
            for (const item of itemsToSync) {
                if (!self.items.has(item.id!)) {
                    self.items.put(item);
                }
            }
            const newItemsSet = new Set(itemsToSync.map(item => item.id));
            for (const itemMST of self.items.values()) {
                if (!newItemsSet.has(itemMST.id)) {
                    self.removeItemSync(itemMST.id);
                }
            }
        },
        addChild(item_id: string, index?: number) {
            if (index !== undefined) {
                self.childIds.splice(index, 0, item_id);
            } else {
                self.childIds.push(item_id);
            }
        },
        removeChild(item_id: string) {
            self.childIds.remove(item_id);
        },
        setChildIds(item_ids: string[]) {
            // @ts-ignore
            self.childIds = item_ids;
        },
        addChildTrash(item_id: string) {
            self.trashedIds.push(item_id);
        },
        removeChildTrash(item_id: string) {
            self.trashedIds.remove(item_id);
        },
        setChildTrashIds(item_ids: string[]) {
            // @ts-ignore
            self.trashedIds = item_ids;
        },
        addChildArchived(item_id: string) {
            self.archivedIds.push(item_id);
        },
        removeChildArchived(item_id: string) {
            self.archivedIds.remove(item_id);
        },
        setChildArchivedIds(item_ids: string[]) {
            // @ts-ignore
            self.archivedIds = item_ids;
        },
        addItem(newItem: ItemModelType, parentId: string | null = null, noSelect: boolean = false, index?: number) {
            let waitAsync: Promise<any>;
            self.items.put(newItem);
            const {getServiceRegistry, getDsManager} = getEnv<RootStoreEnv>(self);
            const registry = getServiceRegistry();
            const crudServices = {
                authService: registry.getAuthService(),
                dataClientService: registry.getDataClientService(),
            };
            const deps = {
                ...crudServices,
                dsManager: getDsManager()!,
            };

            let parentElement = parentId === null ? null : self.items.get(parentId)!;
            if (parentElement === null) {
                if (newItem.isLibrary) {
                    waitAsync = updateWorkspaceChildIdsAdd(deps, self as WorkspaceModelType, newItem.id, index);
                } else {
                    const libraryId = getDefaultCreateItemParentId(self as WorkspaceModelType);
                    const libraryItem = self.items.get(libraryId)!;
                    waitAsync = updateItemChildIdsAdd(crudServices, deps.dsManager, libraryItem, newItem.id, self as WorkspaceModelType, index);
                    if (!noSelect) {
                        libraryItem.setExpanded(true);
                    }
                }
            } else if (parentElement.isCluster || parentElement.isLibrary) {
                assertCanContainChild(parentElement, newItem.type as ItemType);
                waitAsync = updateItemChildIdsAdd(crudServices, deps.dsManager, parentElement, newItem.id, self as WorkspaceModelType, index);
                if (!noSelect) {
                    parentElement.setExpanded(true);
                }
            } else {
                const clusterId = parentElement.firstParentId;
                if (clusterId === null) {
                    const libraryId = getDefaultCreateItemParentId(self as WorkspaceModelType);
                    const libraryItem = self.items.get(libraryId)!;
                    waitAsync = updateItemChildIdsAdd(crudServices, deps.dsManager, libraryItem, newItem.id, self as WorkspaceModelType, index);
                } else {
                    const clusterItem = self.items.get(clusterId)!;
                    if (!noSelect) {
                        parentElement.setExpanded(true);
                    }
                    waitAsync = updateItemChildIdsAdd(crudServices, deps.dsManager, clusterItem, newItem.id, self as WorkspaceModelType, index);
                }
            }
            return waitAsync;
        },
        removeItem(targetItemId: string) {
            const {getServiceRegistry, getDsManager} = getEnv<RootStoreEnv>(self);
            const registry = getServiceRegistry();
            const crudServices = {
                authService: registry.getAuthService(),
                dataClientService: registry.getDataClientService(),
            };
            const deps = {
                ...crudServices,
                dsManager: getDsManager()!,
            };

            let waitAffected = Promise.resolve();
            if (self.trashedIds.includes(targetItemId)) {
                waitAffected = updateWorkspaceTrashedIdsRemove(deps, self as WorkspaceModelType, targetItemId);
            }
            if (self.childIds.includes(targetItemId)) {
                waitAffected = updateWorkspaceChildIdsRemove(deps, self as WorkspaceModelType, targetItemId);
            }
            if (self.archivedIds.includes(targetItemId)) {
                waitAffected = updateWorkspaceArchivedIdsRemove(deps, self as WorkspaceModelType, targetItemId);
            }
            self.removeItemSync(targetItemId);
            return waitAffected;
        },
    }))

export type ItemModelType = Instance<typeof ItemModel>
export type WorkspaceModelType = Instance<typeof WorkspaceModel>

export interface WorkspaceModelSnapshotIn extends SnapshotIn<typeof WorkspaceModel> {
}

export interface WorkspaceModelSnapshotOut extends SnapshotOut<typeof WorkspaceModel> {
}

export interface ItemModelSnapshotIn extends SnapshotIn<typeof ItemModel> {
}

export interface ItemModelSnapshotOut extends SnapshotOut<typeof ItemModel> {
}

function getVisibleLibraries(workspace: any): ItemModelType[] {
    const childOrder: string[] = workspace.childIds.toJSON ? workspace.childIds.toJSON() : Array.from(workspace.childIds);
    const childOrderIndex = new Map(childOrder.map((id: string, index: number) => [id, index]));

    return (Array.from(workspace.items.values()) as ItemModelType[])
        .filter((item: ItemModelType) => item.isLibrary && !item.trashed && !item.archived)
        .sort((a: ItemModelType, b: ItemModelType) => {
            const aIndex = childOrderIndex.get(a.id);
            const bIndex = childOrderIndex.get(b.id);

            if (aIndex === undefined && bIndex === undefined) {
                return a.name.localeCompare(b.name);
            }
            if (aIndex === undefined) {
                return 1;
            }
            if (bIndex === undefined) {
                return -1;
            }
            return aIndex - bIndex;
        });
}

function getStatusSourceLibraryIdForItem(
    workspace: any,
    itemId: string,
    visited: Set<string> = new Set(),
): string | null {
    if (visited.has(itemId)) {
        return null;
    }
    visited.add(itemId);

    const item = workspace.items.get(itemId);
    if (!item) {
        return null;
    }

    if (isVisibleLibraryId(workspace, item.statusSourceLibraryId)) {
        return item.statusSourceLibraryId;
    }

    const currentLibraryId = findCurrentLibraryIdForItem(workspace, item);
    if (currentLibraryId) {
        return currentLibraryId;
    }

    for (const parentId of item.parentIds) {
        const parent = workspace.items.get(parentId);
        if (!parent) {
            continue;
        }
        if (isVisibleLibraryId(workspace, parent.statusSourceLibraryId)) {
            return parent.statusSourceLibraryId;
        }
        const parentSourceLibraryId = getStatusSourceLibraryIdForItem(workspace, parent.id, visited);
        if (parentSourceLibraryId) {
            return parentSourceLibraryId;
        }
    }

    return null;
}

function findCurrentLibraryIdForItem(workspace: any, item: ItemModelType): string | null {
    if (isVisibleLibraryId(workspace, item.id)) {
        return item.id;
    }

    const libraryIds = new Set(getVisibleLibraries(workspace).map(library => library.id));
    const directLibraryParentId = item.parentIds.find(parentId => libraryIds.has(parentId));
    if (directLibraryParentId) {
        return directLibraryParentId;
    }

    const pathWithLibrary = item.activeParentPaths.find(parentPath =>
        parentPath.some(parentId => libraryIds.has(parentId))
    );

    return pathWithLibrary?.find(parentId => libraryIds.has(parentId)) ?? null;
}

function isVisibleLibraryId(workspace: any, libraryId: string | null | undefined): libraryId is string {
    if (!libraryId) {
        return false;
    }

    const library = workspace.items.get(libraryId);
    return !!library && library.isLibrary && !library.trashed && !library.archived;
}
